import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import {
  deleteHabit,
  deleteHabitById,
  getHabitsWithCompletions,
  updateHabitById,
} from "../store/slices/habitsSlice";
import ConfirmationModal from "./ConfirmationModal";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { DayOfWeek } from "../types";
import { calculateCurrentStreak } from "../utils/dateUtils";

const getInitials = (
  name?: string | null,
  username?: string | null,
): string => {
  if (name)
    return name
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  if (username) return username.charAt(0).toUpperCase();
  return "U";
};

const HabitList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const userProfile = useAppSelector((state) => state.user.profile);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {
    items: habits,
    completions,
    loading,
  } = useAppSelector((state) => state.habits);
  const hasFetchedRef = useRef(false);
  const initials = getInitials(userProfile?.name, userProfile?.username);

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // State for confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(
    null,
  );

  useEffect(() => {
    trackPageVisit("habit_list", {
      total_habits: habits.length,
      active_habits: habits.filter((h) => h.isActive).length,
    });
  }, [habits]);

  const fetchHabitsData = useCallback(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    // Calculate date range for habits with completions (3 months ago to 1 month ahead)
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1)
      .toISOString()
      .split("T")[0];
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 31)
      .toISOString()
      .split("T")[0];

    // Fetch latest habits with completions
    dispatch(getHabitsWithCompletions({ startDate, endDate }));
  }, [dispatch]);

  useEffect(() => {
    fetchHabitsData();
  }, [fetchHabitsData]);

  const activeHabits = useMemo(() => {
    return habits
      .filter((habit) => habit.isActive)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  }, [habits]);

  const getCurrentStreak = useCallback(
    (habitId: string) => {
      const habit = habits.find((h) => h.id === habitId);
      if (!habit) return 0;

      const habitCompletions = completions.filter((c) => c.habitId === habitId);
      return calculateCurrentStreak(habit, habitCompletions);
    },
    [habits, completions],
  );

  const habitsOverview = useMemo(() => {
    const pausedCount = activeHabits.filter((habit) => habit.isPaused).length;
    const bestCurrentStreak = activeHabits.reduce(
      (max, habit) => Math.max(max, getCurrentStreak(habit.id)),
      0,
    );
    const bestEverStreak = activeHabits.reduce(
      (max, habit) => Math.max(max, habit.bestStreak || 0),
      0,
    );

    return {
      total: activeHabits.length,
      pausedCount,
      bestCurrentStreak,
      bestEverStreak,
    };
  }, [activeHabits, getCurrentStreak]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatActiveDays = (activeForDays?: DayOfWeek[]) => {
    if (!activeForDays || activeForDays.length === 0) {
      return t("habitList.everyDay");
    }

    const dayLabels: Record<DayOfWeek, string> = {
      monday: t("habitList.dayLabels.monday"),
      tuesday: t("habitList.dayLabels.tuesday"),
      wednesday: t("habitList.dayLabels.wednesday"),
      thursday: t("habitList.dayLabels.thursday"),
      friday: t("habitList.dayLabels.friday"),
      saturday: t("habitList.dayLabels.saturday"),
      sunday: t("habitList.dayLabels.sunday"),
    };

    return activeForDays.map((day) => dayLabels[day]).join(", ");
  };

  const handleBack = () => {
    navigate("/app");
  };

  const showConfirmationModal = (message: string, onConfirm: () => void) => {
    setModalMessage(message);
    setOnConfirmAction(() => onConfirm);
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (onConfirmAction) {
      onConfirmAction();
    }
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setOnConfirmAction(null);
  };

  const handleEditHabit = (habitId: string) => {
    navigate(`/edit-habit/${habitId}`);
  };

  const handleViewDetails = (habitId: string) => {
    navigate(`/habit/${habitId}`);
  };

  const handleDeleteHabit = (habitId: string) => {
    showConfirmationModal(t("habitList.deleteConfirmation"), () => {
      // Delete locally first for immediate UI feedback
      dispatch(deleteHabit(habitId));
      // Then delete on backend
      dispatch(deleteHabitById(habitId));

      // Track habit deletion event
      trackUserInteraction("delete_habit", "habit_management", habitId);
    });
  };

  const handleTogglePause = async (habitId: string, isPaused: boolean) => {
    try {
      await dispatch(
        updateHabitById({
          id: habitId,
          updates: { isPaused },
        }),
      );
    } catch (error) {
      console.error("Error updating habit pause status:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f5a623] rounded-2xl mb-6">
            <svg
              className="animate-spin w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            {t("habitList.loadingHabits")}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={t("habitList.title")}
        description={t("habitList.description") as string}
        keywords={t("habitList.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("habitList.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleBack}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("habitList.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4 lg:py-5">
            <div className="max-w-5xl mx-auto pb-8 space-y-4 lg:space-y-5">
              {activeHabits.length > 0 && (
                <>
                  <section className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.05] p-3 lg:p-4 text-center">
                      <span className="text-base lg:text-xl block mb-1">
                        🔥
                      </span>
                      <span className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white block">
                        {habitsOverview.total}
                      </span>
                      <span className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5 block">
                        {t("habitList.activeStatus")}
                      </span>
                    </div>
                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.05] p-3 lg:p-4 text-center">
                      <span className="text-base lg:text-xl block mb-1">
                        ⏸️
                      </span>
                      <span className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white block">
                        {habitsOverview.pausedCount}
                      </span>
                      <span className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5 block">
                        {t("editHabit.habitPaused")}
                      </span>
                    </div>
                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.05] p-3 lg:p-4 text-center">
                      <span className="text-base lg:text-xl block mb-1">
                        ⚡
                      </span>
                      <span className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white block">
                        {habitsOverview.bestCurrentStreak}
                      </span>
                      <span className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5 block">
                        {t("habitList.currentStreak")}
                      </span>
                    </div>
                    <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.05] p-3 lg:p-4 text-center">
                      <span className="text-base lg:text-xl block mb-1">
                        🏆
                      </span>
                      <span className="text-[21px] lg:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white block">
                        {habitsOverview.bestEverStreak}
                      </span>
                      <span className="text-[13px] lg:text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30 mt-0.5 block">
                        {t("habitList.bestStreak")}
                      </span>
                    </div>
                  </section>
                  <hr className="mt-1 mb-1 mx-2 content-center opacity-30" />
                </>
              )}

              {activeHabits.length === 0 ? (
                <div className="rounded-2xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] p-10 lg:p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl border border-black/[0.07] dark:border-white/[0.08] bg-black/[0.03] dark:bg-white/[0.05] flex items-center justify-center mx-auto mb-6">
                    <svg
                      className="w-8 h-8 text-gray-400 dark:text-white/35"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t("habitList.noActiveHabits")}
                  </h3>
                  <p className="text-gray-600 dark:text-white/40">
                    {t("habitList.noHabitsMessage")}
                  </p>
                </div>
              ) : (
                <section className="grid grid-cols-1 xl:grid-cols-2 gap-3 lg:gap-4">
                  {activeHabits.map((habit) => (
                    <article
                      key={habit.id}
                      className="rounded-2xl border border-black/[0.07] dark:border-white/[0.07] bg-white dark:bg-white/[0.04] p-4 lg:p-5 cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.06] transition-all duration-150"
                      onClick={() => handleViewDetails(habit.id)}
                    >
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="min-w-0">
                          <h3 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                            {habit.name}
                          </h3>
                          <p className="text-[15px] text-gray-500 dark:text-white/35 mt-1 truncate">
                            {t("habitList.active")}{" "}
                            {formatActiveDays(habit.activeForDays)}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[15px] font-medium border ${
                            habit.isPaused
                              ? "text-[#e87b3f] bg-[#e87b3f]/12 border-[#e87b3f]/30"
                              : "text-[#f5a623] bg-[#f5a623]/12 border-[#f5a623]/30"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${habit.isPaused ? "bg-[#e87b3f]" : "bg-[#f5a623]"}`}
                          ></span>
                          {habit.isPaused
                            ? t("editHabit.habitPaused")
                            : t("habitList.activeStatus")}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-black/[0.03] dark:bg-white/[0.05] px-3 py-2.5">
                          <p className="text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30">
                            {t("habitList.currentStreak")}
                          </p>
                          <p className="text-[18px] font-semibold text-gray-900 dark:text-white mt-0.5">
                            {getCurrentStreak(habit.id)} {t("habitList.days")}
                          </p>
                        </div>
                        <div className="rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-black/[0.03] dark:bg-white/[0.05] px-3 py-2.5">
                          <p className="text-[14px] uppercase tracking-wider text-gray-400 dark:text-white/30">
                            {t("habitList.bestStreak")}
                          </p>
                          <p className="text-[18px] font-semibold text-gray-900 dark:text-white mt-0.5">
                            {habit.bestStreak} {t("habitList.days")}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-1.5 mb-4">
                        <p className="text-[16px] text-gray-500 dark:text-white/35">
                          {t("habitList.created")}{" "}
                          <span className="font-medium text-gray-900 dark:text-white/85">
                            {formatDate(habit.createdAt)}
                          </span>
                        </p>
                        {habit.lastCompleted && (
                          <p className="text-[16px] text-gray-500 dark:text-white/35">
                            {t("habitList.lastCompleted")}{" "}
                            <span className="font-medium text-gray-900 dark:text-white/85">
                              {habit.lastCompleted}
                            </span>
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePause(habit.id, !habit.isPaused);
                          }}
                          className={`h-9 w-9 rounded-xl border transition-colors flex items-center justify-center ${
                            habit.isPaused
                              ? "text-[#e87b3f] border-[#e87b3f]/30 bg-[#e87b3f]/10 hover:bg-[#e87b3f]/18"
                              : "text-gray-500 dark:text-white/45 border-black/[0.08] dark:border-white/[0.08] bg-black/[0.03] dark:bg-white/[0.05] hover:bg-black/[0.06] dark:hover:bg-white/[0.09]"
                          }`}
                          title={
                            habit.isPaused ? "Resume habit" : "Pause habit"
                          }
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            {habit.isPaused ? (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14.751 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.554.832l3.197-2.132a1 1 0 000-1.664z"
                              />
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            )}
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(habit.id);
                          }}
                          className="h-9 w-9 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-black/[0.03] dark:bg-white/[0.05] text-gray-500 dark:text-white/45 hover:text-[#3b5cde] hover:bg-[#3b5cde]/12 transition-colors flex items-center justify-center"
                          title="View details"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditHabit(habit.id);
                          }}
                          className="h-9 w-9 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-black/[0.03] dark:bg-white/[0.05] text-gray-500 dark:text-white/45 hover:text-[#22c67a] hover:bg-[#22c67a]/12 transition-colors flex items-center justify-center"
                          title="Edit habit"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteHabit(habit.id);
                          }}
                          className="h-9 w-9 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-black/[0.03] dark:bg-white/[0.05] text-gray-500 dark:text-white/45 hover:text-[#e23f5d] hover:bg-[#e23f5d]/12 transition-colors flex items-center justify-center"
                          title="Delete habit"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </article>
                  ))}
                </section>
              )}

              <ConfirmationModal
                isOpen={isModalOpen}
                message={modalMessage}
                onConfirm={handleModalConfirm}
                onCancel={handleModalCancel}
              />
            </div>
          </div>
        </main>

        {/* Floating Add Habit Button */}
        <button
          onClick={() => navigate("/add-habit")}
          className="fixed bottom-24 right-8 z-40 bg-[#f5a623] hover:opacity-90 text-[#1a1300] p-4 rounded-full border border-[#f5a623] transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#f5a623] focus:ring-offset-2"
          aria-label={t("habitList.addHabitButton") as string}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
        </button>

        {/* MOBILE BOTTOM NAV */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 backdrop-blur-sm border-t border-black/[0.08] dark:border-white/[0.08]">
          <div className="flex items-center justify-around px-2 pt-2 pb-2">
            {mobileNavLinks.map(({ path, emoji, label }) => {
              const active = isNavActive(path);
              return (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex flex-col items-center gap-1 cursor-pointer"
                  style={{ color: active ? "#f5a623" : undefined }}
                >
                  <span className="text-[22px]">{emoji}</span>
                  <span
                    className={`text-[13px] font-medium${active ? "" : " text-gray-400 dark:text-white/25"}`}
                    style={active ? { color: "#f5a623" } : undefined}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default HabitList;
